import AddressSearchField from '@components/forms/address-search-field/address-search-field';
import { parseAddressComponents } from '@components/forms/address-search-field/parseAddressComponents';
import { Field, Input, Stack, Text } from '@components/ui';
import { useHireJob } from '@features/buyer/hire/hire-job-context';
import { HireStepActions } from '@features/buyer/hire/hire-r8tr/hire-step-actions';
import type { HireStepNavigation } from '@features/buyer/hire/hire-r8tr/hireR8trTypes';
import OpenInNewIcon from '@mui/icons-material/OpenInNew';
import Box from '@mui/material/Box';
import { useEffect, useState } from 'react';
import { useForm } from 'react-hook-form';
import { useTranslation } from 'react-i18next';
import { geocodeByAddress, getLatLng } from 'react-places-autocomplete';

import type {
  AddressFormState,
  AddressFormValues,
} from './addressDetailsTypes';
interface AddressDetailsProps extends HireStepNavigation {
  onPressNext: () => void;
  handleTosterFire: (message: string) => void;
}

const AddressDetails = ({
  onPressNext,
  handleTosterFire,
  onBack,
  onCancel,
}: AddressDetailsProps): JSX.Element => {
  const { t } = useTranslation();
  const { createJob, saveJob } = useHireJob();
  const {
    register,
    setValue,
    trigger,
    formState: { isValid },
  } = useForm<AddressFormValues>({
    mode: 'onChange',
    defaultValues: {
      address: '',
      listing: '',
    },
  });
  const [form, setForm] = useState<AddressFormState>({
    listing: '',
    address: '',
    latitude: null,
    longitude: null,
    city: '',
    state: '',
    zip: '00000',
  });

  const isNonAutomotive = createJob?.isAutomotive === false;
  const addressLabel = isNonAutomotive
    ? t('hire.itemAddressRequired')
    : t('hire.vehicleAddressRequired');

  const mapQuery =
    form.latitude && form.longitude
      ? `${form.latitude},${form.longitude}`
      : form.address;
  const mapLink = mapQuery
    ? `https://maps.google.com?q=${encodeURIComponent(mapQuery)}`
    : null;
  const mapEmbedSrc = mapQuery
    ? `https://maps.google.com/maps?q=${encodeURIComponent(
        mapQuery
      )}&z=15&output=embed`
    : null;

  useEffect(() => {
    register('address', { required: true });
    register('listing');
  }, [register]);

  useEffect(() => {
    setForm((prev) => ({
      ...prev,
      listing: createJob.listing || '',
      address: createJob.address || '',
      city: createJob.city || '',
      state: createJob.state || '',
      zip: createJob.zip || '00000',
      latitude: createJob.jobLatitude ?? null,
      longitude: createJob.jobLongitude ?? null,
    }));
    setValue('listing', createJob.listing || '', { shouldValidate: true });
    setValue('address', createJob.address || '', { shouldValidate: true });
  }, [createJob, setValue]);

  const handleSelectAddress = async (jobAddress: string): Promise<void> => {
    if (!jobAddress) return;
    try {
      const results = await geocodeByAddress(jobAddress);
      const latLng = await getLatLng(results[0]);
      const parsed = parseAddressComponents(results[0]);

      setForm((prev) => ({
        ...prev,
        address: jobAddress,
        city: parsed.city,
        state: parsed.state,
        zip: parsed.zip || '00000',
        latitude: latLng.lat,
        longitude: latLng.lng,
      }));
      setValue('address', jobAddress, { shouldValidate: true });
    } catch {
      setForm((prev) => ({
        ...prev,
        address: '',
        latitude: null,
        longitude: null,
      }));
      setValue('address', '', { shouldValidate: true });
    }
  };

  const handleChangeAddress = (address: string): void => {
    setForm((prev) => ({
      ...prev,
      address,
      latitude: null,
      longitude: null,
      city: '',
      state: '',
      zip: '00000',
    }));
    setValue('address', address, { shouldValidate: true });
    trigger('address');
  };

  const handleSubmit = async (): Promise<void> => {
    if (!form.address) {
      handleTosterFire(
        isNonAutomotive
          ? t('hire.enterItemAddress')
          : t('hire.enterVehicleAddress')
      );
      return;
    }

    let latitude = form.latitude;
    let longitude = form.longitude;
    let resolvedCity = form.city;
    let resolvedState = form.state;
    let resolvedZip = form.zip;

    if (!latitude || !longitude) {
      try {
        const results = await geocodeByAddress(form.address);
        const latLng = await getLatLng(results[0]);
        const parsed = parseAddressComponents(results[0]);

        latitude = latLng.lat;
        longitude = latLng.lng;
        resolvedState = parsed.state;
        resolvedCity = parsed.city;
        resolvedZip = parsed.zip;
      } catch {
        handleTosterFire(
          t('hire.invalidAddress', { address: form.address })
        );
        return;
      }
    }

    const addressPatch = {
      listing: form.listing,
      link: form.listing,
      address: form.address,
      city: resolvedCity,
      state: resolvedState,
      zip: resolvedZip,
      jobLatitude: latitude,
      jobLongitude: longitude,
      latitude,
      longitude,
    };
    saveJob(addressPatch);
    onPressNext();
  };

  const chipSx = {
    display: 'inline-flex',
    alignItems: 'center',
    borderRadius: 999,
    border: 1,
    borderColor: 'divider',
    bgcolor: 'background.default',
    px: 1.25,
    py: 0.5,
    fontSize: 11,
    fontWeight: 600,
    letterSpacing: '0.3px',
    color: 'text.secondary',
  };

  return (
    <Stack gap="xs" sx={{ flex: 1 }}>
      <Field label={addressLabel}>
        <AddressSearchField
          value={form.address}
          onChange={handleChangeAddress}
          onSelect={handleSelectAddress}
          placeholder={t('hire.addressPlaceholder')}
          searchOptions={{
            types: ['street_address', 'subpremise', 'premise'],
          }}
        />
        {form.city || form.state || (form.zip && form.zip !== '00000') ? (
          <Box sx={{ mt: 1, display: 'flex', flexWrap: 'wrap', gap: 0.75 }}>
            {form.city ? (
              <Box component="span" sx={chipSx}>
                {form.city}
              </Box>
            ) : null}
            {form.state ? (
              <Box component="span" sx={chipSx}>
                {form.state}
              </Box>
            ) : null}
            {form.zip && form.zip !== '00000' ? (
              <Box component="span" sx={chipSx}>
                {form.zip}
              </Box>
            ) : null}
          </Box>
        ) : null}
      </Field>

      {mapLink && mapEmbedSrc ? (
        <Box
          sx={{
            overflow: 'hidden',
            borderRadius: 2,
            border: 1,
            borderColor: 'divider',
            bgcolor: 'background.default',
            mb: 2,
          }}
        >
          <Box
            sx={{
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'space-between',
              gap: 1.25,
              borderBottom: 1,
              borderColor: 'divider',
              px: 2,
              py: 1.25,
            }}
          >
            <Text variant="label" tone="muted">
              {t('hire.mapPreviewLabel')}
            </Text>
            <Box
              component="a"
              href={mapLink}
              target="_blank"
              rel="noopener noreferrer"
              sx={{
                display: 'inline-flex',
                alignItems: 'center',
                gap: 0.5,
                fontSize: 13,
                fontWeight: 600,
                color: 'text.primary',
                textDecoration: 'none',
                '&:hover': { textDecoration: 'underline' },
              }}
            >
              {t('hire.openInMaps')}
              <OpenInNewIcon sx={{ fontSize: 14 }} />
            </Box>
          </Box>
          <Box
            component="a"
            href={mapLink}
            target="_blank"
            rel="noopener noreferrer"
            aria-label={t('hire.openMapAria')}
            sx={{ display: 'block', lineHeight: 0 }}
          >
            <Box
              component="iframe"
              title={t('hire.locationMapPreview')}
              src={mapEmbedSrc}
              loading="lazy"
              referrerPolicy="no-referrer-when-downgrade"
              sx={{
                display: 'block',
                height: 240,
                width: '100%',
                border: 0,
                filter: 'saturate(0.92) contrast(1.04)',
              }}
            />
          </Box>
        </Box>
      ) : null}

      <Field label={t('hire.linkToListingRecommended')}>
        <Input
          type="url"
          placeholder={t('hire.linkToListingPlaceholder')}
          value={form.listing}
          onChange={(event) => {
            setForm((prev) => ({ ...prev, listing: event.target.value }));
            setValue('listing', event.target.value, { shouldValidate: true });
          }}
        />
      </Field>

      <HireStepActions
        onBack={onBack}
        onCancel={onCancel}
        onPrimary={() => {
          void handleSubmit();
        }}
        primaryDisabled={!isValid}
      />
    </Stack>
  );
};

export default AddressDetails;
